import React, { useState } from "react";
import { CheckCircle2, ArrowRight, FileText, Calendar, IndianRupee, ClipboardCheck } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { courses } from "../mock/mock";
import { PageHero } from "./About";

const Admissions = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    course: "",
    qualification: "",
    percentage: "",
    address: "",
    message: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    // frontend-only save
    const saved = JSON.parse(localStorage.getItem("applications") || "[]");
    saved.push({ ...form, id: Date.now(), submittedAt: new Date().toISOString() });
    localStorage.setItem("applications", JSON.stringify(saved));
    toast({
      title: "Application submitted",
      description: "Thank you! Our admissions team will contact you shortly.",
    });
    setForm({
      name: "",
      email: "",
      phone: "",
      dob: "",
      gender: "",
      course: "",
      qualification: "",
      percentage: "",
      address: "",
      message: "",
    });
  };

  const steps = [
    { icon: FileText, title: "Fill Application", text: "Complete the online admission enquiry form." },
    { icon: ClipboardCheck, title: "Document Review", text: "Submit certificates for eligibility verification." },
    { icon: Calendar, title: "Counselling", text: "Attend a brief counselling session with our team." },
    { icon: IndianRupee, title: "Fee Payment", text: "Complete admission by paying the fee at the office." },
  ];

  return (
    <div>
      <PageHero
        eyebrow="Admissions Open 2025"
        title="Join us. Shape the future."
        subtitle="Applications are open for the 2025-27 batch. Reserve your seat today."
        image="https://images.unsplash.com/photo-1541339907198-e08756dedf3f"
      />

      {/* Steps */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Admission Process</div>
            <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep">Four simple steps</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div
                key={i}
                className="relative bg-brand-ivory border border-brand-gold/25 rounded-sm p-6 hover-lift animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-brand-green-deep text-brand-gold font-serif-display font-bold flex items-center justify-center border-2 border-brand-gold">
                  {i + 1}
                </div>
                <s.icon className="w-9 h-9 text-brand-gold-dark mb-4" />
                <h3 className="font-serif-display text-xl text-brand-green-deep mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="section-y bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Eligibility</div>
            <h2 className="font-serif-display text-4xl font-bold text-brand-green-deep mb-6">
              Who can <span className="text-brand-gold-dark italic">apply?</span>
            </h2>
            <ul className="space-y-3 text-neutral-700">
              {[
                "Passed 10+2 (any stream) from a recognized board",
                "Minimum 50% aggregate (45% for reserved categories)",
                "Age between 17 and 35 years as on the date of admission",
                "Genuine interest in the teaching profession",
                "Valid ID proof and address proof",
              ].map((e) => (
                <li key={e} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 p-5 bg-brand-green-deep text-brand-ivory rounded-sm border-l-4 border-brand-gold">
              <div className="text-xs uppercase tracking-widest text-brand-gold mb-1">Note</div>
              <p className="text-sm text-brand-ivory/85">
                Reservations for SC/ST/OBC candidates as per Government of Karnataka norms. Please contact our office for scholarship information.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-sm border border-brand-gold/30 p-6 md:p-8 shadow-xl">
            <h3 className="font-serif-display text-2xl text-brand-green-deep mb-1">Admission Enquiry Form</h3>
            <p className="text-sm text-neutral-600 mb-6">Fill in your details and our team will get in touch.</p>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: "name", label: "Full Name", type: "text", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone", type: "tel", required: true },
                { name: "dob", label: "Date of Birth", type: "date" },
              ].map((f) => (
                <div key={f.name} className="flex flex-col gap-1.5">
                  <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">{f.label}</label>
                  <input
                    name={f.name}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={onChange}
                    className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                  />
                </div>
              ))}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Gender</label>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={onChange}
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                >
                  <option value="">Select</option>
                  <option>Female</option>
                  <option>Male</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Course</label>
                <select
                  name="course"
                  value={form.course}
                  onChange={onChange}
                  required
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                >
                  <option value="">Select a course</option>
                  {courses.map((c) => (
                    <option key={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Last Qualification</label>
                <input
                  name="qualification"
                  value={form.qualification}
                  onChange={onChange}
                  placeholder="e.g. 10+2 PCM"
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">% Marks</label>
                <input
                  name="percentage"
                  value={form.percentage}
                  onChange={onChange}
                  placeholder="e.g. 78%"
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Address</label>
                <input
                  name="address"
                  value={form.address}
                  onChange={onChange}
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Message (optional)</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={3}
                  className="px-4 py-2.5 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                />
              </div>
              <button
                type="submit"
                className="md:col-span-2 btn-primary mt-2 px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                Submit Application <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
